import { useUpdateMyAnalyticsSettings } from '@/api/mutations/user/useUpdateMyAnalyticsSettings';
import useGetMyAnalyticsSettings from '@/api/queries/me/useGetMyAnalyticsSettings';
import useGetMyCustomFieldGroups from '@/api/queries/me/useGetMyCustomFieldGroups';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AnchorButton,
  Card,
  Conditional,
  Field,
  Fieldset,
  Form,
  Select,
  useToastContext,
} from '@payaca/ambrosia';

import { CustomFieldDefinition } from '@payaca/custom-fields';
import { getTerminalFieldPathsWithDescription } from '@payaca/custom-fields/paths';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import z from 'zod/v4';

const setPersonalAnalyticsSettingsSchema = z.object({
  projectCustomField1Path: z.string().nullable(),
  projectCustomField2Path: z.string().nullable(),
  projectCustomField3Path: z.string().nullable(),
});

type SetPersonalAnalyticsSettings = z.output<
  typeof setPersonalAnalyticsSettingsSchema
>;

export const PersonalAnalyticsSettings: FC = () => {
  const { pushSuccess, pushError } = useToastContext();
  const analyticsSettings = useGetMyAnalyticsSettings();
  const updateAnalyticsSettings = useUpdateMyAnalyticsSettings();
  const customFieldGroups = useGetMyCustomFieldGroups();

  const flattenedProjectCustomFields = [
    {
      path: '',
      description: '-',
    },
    ...(customFieldGroups.data
      ?.filter((group) => group.role === 'project')
      .flatMap((group) =>
        group.customFields
          .filter((field) => !field.archivedAt)
          .flatMap((field) =>
            getTerminalFieldPathsWithDescription(
              CustomFieldDefinition.fromSchema(field.schema)
            )
          )
      ) ?? []),
  ];
  const accountHasProjectCustomFields = customFieldGroups.data
    ? flattenedProjectCustomFields.length > 1
    : null;

  const form = useForm<SetPersonalAnalyticsSettings>({
    values: analyticsSettings.data,
    resolver: zodResolver(setPersonalAnalyticsSettingsSchema),
  });

  const saveChanges = async (formData: SetPersonalAnalyticsSettings) => {
    try {
      await updateAnalyticsSettings.mutateAsync({
        projectCustomField1Path: formData.projectCustomField1Path ?? null,
        projectCustomField2Path: formData.projectCustomField2Path ?? null,
        projectCustomField3Path: formData.projectCustomField3Path ?? null,
      });
      pushSuccess('Saved successfully');
    } catch {
      pushError('Failed to save settings');
    } finally {
      await analyticsSettings.refetch();
    }
  };

  // Submit form whenever values change
  useEffect(() => {
    const subscription = form.watch((_value, { type }) => {
      if (type === 'change') {
        void form.handleSubmit(saveChanges)();
      }
    });
    return () => subscription.unsubscribe();
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'saveChanges'
  }, [form]);

  function SelectAnalyticsCustomField<
    N extends
      | 'projectCustomField1Path'
      | 'projectCustomField2Path'
      | 'projectCustomField3Path',
  >(props: { name: N; label: string }) {
    return (
      <Controller<SetPersonalAnalyticsSettings, N>
        name={props.name}
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <Field name={props.name}>
            <Field.Label>{props.label}</Field.Label>
            <Select
              options={flattenedProjectCustomFields.map((field) => ({
                value: field.path,
                label: field.description,
              }))}
              placeholder="Select a Custom Field"
              onChange={onChange}
              value={value ?? undefined}
              disabled={
                updateAnalyticsSettings.isPending ||
                !analyticsSettings.isSuccess ||
                !customFieldGroups.isSuccess
              }
            />
          </Field>
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-4 w-full max-w-[1200px] m-auto">
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Custom Fields for Projects</h2>
          <p className="mb-4">
            Choose which Custom Fields you will see in Analytics dashboards.
            These fields can be used to filter each dashboard, and their data
            will be included in any table charts.
          </p>
          <Conditional
            condition={accountHasProjectCustomFields !== false}
            fallback={
              <Alert colour="yellow">
                It looks like you don&apos;t have any Custom Fieldsets for
                Projects yet.{' '}
                <PermissionGuard
                  renderIfHasPermissions={[
                    AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
                  ]}
                >
                  Go to{' '}
                  <AnchorButton
                    as={Link}
                    to="/companySettings/custom-fields"
                    variant="link-inline"
                  >
                    your company settings
                  </AnchorButton>{' '}
                  to get started.
                </PermissionGuard>
              </Alert>
            }
          >
            <Form methods={form} onSubmit={saveChanges}>
              <Fieldset className="mb-2">
                <SelectAnalyticsCustomField
                  name="projectCustomField1Path"
                  label="Custom Field #1"
                />
                <SelectAnalyticsCustomField
                  name="projectCustomField2Path"
                  label="Custom Field #2"
                />
                <SelectAnalyticsCustomField
                  name="projectCustomField3Path"
                  label="Custom Field #3"
                />
              </Fieldset>
            </Form>
          </Conditional>
        </Card.Body>
      </Card>
    </div>
  );
};
